import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceTabs = ({ service }) => {
  const [activeTab, setActiveTab] = useState('specifications');

  const tabs = [
    { id: 'specifications', label: 'Specifications', icon: 'FileText' },
    { id: 'implementation', label: 'Implementation', icon: 'Settings' },
    { id: 'case-studies', label: 'Case Studies', icon: 'BookOpen' },
    { id: 'pricing', label: 'Pricing Guidelines', icon: 'DollarSign' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'specifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Technical Specifications</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {service?.specifications?.map((spec, index) => (
                  <div key={index} className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-2">{spec?.category}</h4>
                    <ul className="space-y-1">
                      {spec?.items?.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-sm text-muted-foreground flex items-start space-x-2">
                          <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">System Requirements</h3>
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Minimum Requirements</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {service?.requirements?.minimum?.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Recommended</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {service?.requirements?.recommended?.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Enterprise</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      {service?.requirements?.enterprise?.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'implementation':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Implementation Process</h3>
              <div className="space-y-4">
                {service?.implementationSteps?.map((step, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground mb-1">{step?.title}</h4>
                      <p className="text-sm text-muted-foreground mb-2">{step?.description}</p>
                      <div className="text-xs text-muted-foreground">
                        <Icon name="Clock" size={12} className="inline mr-1" />
                        Duration: {step?.duration}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Timeline Estimate</h3>
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">{service?.timeline?.planning}</div>
                    <div className="text-sm text-muted-foreground">Planning Phase</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">{service?.timeline?.implementation}</div>
                    <div className="text-sm text-muted-foreground">Implementation</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-2">{service?.timeline?.deployment}</div>
                    <div className="text-sm text-muted-foreground">Deployment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'case-studies':
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">Success Stories</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {service?.caseStudies?.map((study, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Building" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{study?.client}</h4>
                      <p className="text-sm text-muted-foreground">{study?.industry}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-4">{study?.challenge}</p>
                  
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-medium text-foreground text-sm mb-1">Solution</h5>
                      <p className="text-sm text-muted-foreground">{study?.solution}</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-foreground text-sm mb-2">Results</h5>
                      <div className="grid grid-cols-2 gap-3">
                        {study?.results?.map((result, resultIndex) => (
                          <div key={resultIndex} className="text-center bg-muted/50 rounded-lg p-3">
                            <div className="text-lg font-bold text-primary">{result?.value}</div>
                            <div className="text-xs text-muted-foreground">{result?.metric}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Pricing Structure</h3>
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {service?.pricingTiers?.map((tier, index) => (
                    <div key={index} className={`border rounded-lg p-6 ${tier?.popular ? 'border-primary bg-primary/5' : 'border-border'}`}>
                      {tier?.popular && (
                        <div className="text-center mb-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary text-primary-foreground">
                            Most Popular
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center mb-6">
                        <h4 className="text-lg font-semibold text-foreground mb-2">{tier?.name}</h4>
                        <div className="text-3xl font-bold text-foreground">
                          {tier?.price}
                          <span className="text-sm font-normal text-muted-foreground">/{tier?.period}</span>
                        </div>
                      </div>
                      
                      <ul className="space-y-2 mb-6">
                        {tier?.features?.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start space-x-2 text-sm">
                            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        variant={tier?.popular ? "default" : "outline"} 
                        fullWidth
                        iconName="ArrowRight"
                        iconPosition="right"
                      >
                        Get Quote
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Additional Information</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>All prices are in UGX and exclude applicable taxes</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Custom enterprise solutions available upon request</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Payment plans and financing options available</span>
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ServiceTabs;